(() => {
  'use strict';
  if (window.MediaTechCart) return;
  const config = JSON.parse(document.querySelector('[data-mt-cart-config]').textContent);
  const labels = config.labels;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const drawer = document.getElementById('mt-cart-drawer');
  const modal = document.getElementById('mt-cart-installments');
  const money = cents => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(cents / 100);
  const termsFor = cents => !Number.isSafeInteger(cents) || cents < 0 ? [] : cents >= 100000 ? [12, 24, 36] : cents >= 50000 ? [12, 24] : cents >= 24000 ? [12] : [];
  const fill = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), labels[key] || key);
  const propertiesOf = value => Array.isArray(value) ? Object.fromEntries(value) : value || {};
  const sorted = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
  const identity = item => `${item.id}:${item.selling_plan_allocation?.selling_plan?.id || item.sellingPlan || ''}:${sorted(propertiesOf(item.properties))}`;
  const fingerprint = cart => JSON.stringify(cart.items.map(item => [identity(item), item.quantity, item.final_price ?? item.price]).sort((a, b) => a[0].localeCompare(b[0])));
  let busy = false;
  let state;
  let selection;
  let originalFocus;
  let previousOverflow;
  const pendingKey = 'mediatech-cart-pending-v1';
  const pendingRead = () => { try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { return null; } };
  const pendingWrite = value => { try { value ? sessionStorage.setItem(pendingKey, JSON.stringify(value)) : sessionStorage.removeItem(pendingKey); } catch {} };
  const url = (path, section) => {
    const result = new URL(`${root}${path}`, location.origin);
    if (section) result.searchParams.set('section_id', section);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) result.searchParams.set('preview_theme_id', preview);
    return result;
  };
  async function request(path, data) {
    const response = await fetch(url(path), { credentials: 'same-origin', cache: 'no-store', ...(data ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) } : {}) });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || labels.unavailable);
    return result;
  }
  const readCart = () => request('cart.js');
  function setError(message = '') {
    document.querySelectorAll('[data-mt-cart-error]').forEach(node => { node.textContent = message; node.hidden = !message; });
  }
  function loading(value) {
    busy = value;
    document.querySelectorAll('[data-mt-cart-content]').forEach(node => { node.inert = value; node.setAttribute('aria-busy', String(value)); });
    document.querySelectorAll('[data-mt-cart-pay]').forEach(node => { node.disabled = value || !selection?.terms?.length; });
    const submit = modal.querySelector('[data-mt-cart-pay-submit]');
    if (submit) submit.disabled = value || !selection || !modal.querySelector('[data-mt-cart-consent]').checked;
    modal.querySelectorAll('input').forEach(node => { node.disabled = value; });
    modal.querySelector('[data-mt-cart-pay-close]').disabled = value;
  }
  async function exclusive(callback) {
    if (busy) return;
    return navigator.locks ? navigator.locks.request('mediatech-cart', callback) : callback();
  }
  function lockScroll() {
    if (previousOverflow !== undefined) return;
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockScroll() {
    if (drawer.open || modal.open || previousOverflow === undefined) return;
    document.documentElement.style.overflow = previousOverflow;
    previousOverflow = undefined;
  }
  function closeDrawer() { drawer.close(); unlockScroll(); }
  function openDrawer() {
    originalFocus = document.activeElement;
    if (!drawer.open) drawer.showModal();
    lockScroll();
    drawer.querySelector('[data-mt-cart-close]').focus({ preventScroll: true });
  }
  async function fetchSource(handle, id) {
    const address = url(`products/${encodeURIComponent(handle)}`, 'mediatech-payment24-verify');
    address.searchParams.set('variant', String(id));
    const response = await fetch(address, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(labels.unavailable);
    const documentPart = new DOMParser().parseFromString(await response.text(), 'text/html');
    const element = documentPart.querySelector('[data-payment24-verification]');
    if (!element || element.dataset.id !== String(id) || element.dataset.currency !== config.currency) throw new Error(labels.changed);
    const data = documentPart.querySelector('[data-mt-source-verification]');
    return { id: Number(id), price: Number(element.dataset.price), available: element.dataset.available === 'true', tracked: element.dataset.tracked === 'true', stock: Number(element.dataset.stock), policy: element.dataset.policy, sku: element.dataset.sku, source: data ? JSON.parse(data.textContent) : null };
  }
  async function limited(items, callback) {
    const results = new Array(items.length);
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(4, items.length) }, async () => {
      while (index < items.length) { const current = index++; results[current] = await callback(items[current], current); }
    }));
    return results;
  }
  async function hydrate(nextState) {
    nextState.items.forEach(item => { item.properties = propertiesOf(item.properties); });
    const cache = new Map();
    const lines = await limited(nextState.items, async item => {
      let source = item;
      if (item.internal || item.properties?._mediatech_payment24) {
        const id = Number(item.properties?._mediatech_source_variant);
        const sourceUrl = item.properties?._mediatech_source_url;
        if (!id || !sourceUrl) throw new Error(labels.unavailable);
        const address = new URL(sourceUrl, location.origin);
        const handle = address.pathname.split('/products/')[1]?.split('/')[0];
        if (!handle) throw new Error(labels.unavailable);
        const key = `${handle}:${id}`;
        if (!cache.has(key)) cache.set(key, fetchSource(handle, id));
        const verified = await cache.get(key);
        if (!verified.source) throw new Error(labels.changed);
        source = { ...verified.source, id, quantity: item.quantity };
      }
      const variant = source.variant?.[String(source.id)];
      if (!variant) return { item, source, variant: null };
      if (item.internal || item.properties?._mediatech_payment24) {
        const oldTerm = Number(item.properties?._mediatech_term || 24);
        const oldPlan = variant.plans?.[String(oldTerm)];
        if (!oldPlan || String(oldPlan.depositId) !== String(item.id) || item.sku !== `M${oldTerm}-${variant.id}-${variant.totalCents}`) throw new Error(labels.changed);
      }
      return { item, source, variant };
    });
    const total = lines.reduce((sum, line) => sum + (line.variant?.totalCents ?? line.item.price) * line.item.quantity, 0);
    const blocked = lines.some(({ item, source, variant }) => !variant || !variant.sourceAvailable || source.requiresSellingPlan || item.sellingPlan || variant.hasQuantityBreaks || (!item.internal && !item.properties?._mediatech_payment24 && item.price !== variant.totalCents));
    const quantities = new Map();
    for (const line of lines) quantities.set(line.variant?.id, (quantities.get(line.variant?.id) || 0) + line.item.quantity);
    const validQuantity = lines.every(({ variant }) => {
      if (!variant) return false;
      const quantity = quantities.get(variant.id);
      return Number.isSafeInteger(quantity) && quantity >= variant.quantityMin && quantity <= 99 && (!variant.quantityMax || quantity <= variant.quantityMax) && (quantity - variant.quantityMin) % variant.quantityIncrement === 0;
    });
    const terms = nextState.currency === 'EUR' && !blocked && validQuantity ? termsFor(total).filter(term => lines.every(({ variant }) => {
      const plan = variant.plans?.[String(term)];
      return plan?.ready && plan.depositId && plan.depositHandle && plan.depositCents === Math.round(variant.totalCents / term) && variant.totalCents - plan.depositCents * (term - 1) > 0;
    })) : [];
    return { lines, total, terms, reason: total < 24000 ? labels.minimum : labels.incompatible };
  }
  function renderPayment() {
    document.querySelectorAll('[data-mt-cart-total]').forEach(node => { node.textContent = money(selection.total); });
    document.querySelectorAll('[data-mt-cart-payment-copy]').forEach(node => { node.textContent = selection.terms.length ? labels.available : selection.reason; });
    document.querySelectorAll('[data-mt-cart-payment-terms]').forEach(node => { node.textContent = selection.terms.length ? fill('availableTerms', { terms: selection.terms.join(', ') }) : ''; });
    document.querySelectorAll('[data-mt-cart-pay]').forEach(node => { node.disabled = busy || !selection.terms.length; });
  }
  async function refresh() {
    const pending = pendingRead();
    if (pending) await recover(pending);
    const response = await fetch(url('cart', 'mediatech-cart-state'), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(labels.unavailable);
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const json = parsed.querySelector('[data-mt-cart-state]');
    const content = parsed.querySelector('[data-mt-cart-render]');
    if (!json || !content) throw new Error(labels.unavailable);
    const nextState = JSON.parse(json.textContent);
    document.querySelectorAll('[data-mt-cart-count]').forEach(node => { node.textContent = String(nextState.count); });
    document.querySelectorAll('[data-mt-cart-content]').forEach(node => { node.innerHTML = content.innerHTML; });
    document.querySelectorAll('.mt-cart-count').forEach(node => node.remove());
    document.querySelectorAll('[data-mt-cart-open]').forEach(node => {
      node.setAttribute('aria-label', fill('cartCount', { count: nextState.count }));
      if (nextState.count) { const badge = document.createElement('span'); badge.className = 'mt-cart-count'; badge.textContent = String(nextState.count); node.append(badge); }
    });
    state = nextState;
    try { selection = await hydrate(nextState); } catch { selection = { total: nextState.total, lines: [], terms: [], reason: labels.incompatible }; }
    renderPayment();
    return selection;
  }
  function summarize(term) {
    const first = selection.lines.reduce((sum, { item, variant }) => sum + variant.plans[String(term)].depositCents * item.quantity, 0);
    return { total: selection.total, first, remaining: selection.total - first, last: selection.total - first * (term - 1) };
  }
  function updateModal() {
    const term = Number(modal.querySelector('[data-mt-cart-term]:checked')?.value);
    if (!selection?.terms.includes(term)) return;
    const summary = summarize(term);
    modal.querySelector('[data-mt-cart-plan-total]').textContent = money(summary.total);
    modal.querySelector('[data-mt-cart-plan-first]').textContent = money(summary.first);
    modal.querySelector('[data-mt-cart-plan-remaining]').textContent = money(summary.remaining);
    modal.querySelector('[data-mt-cart-remaining-label]').textContent = fill('remainingLabel', { count: term - 1 });
    modal.querySelector('[data-mt-cart-schedule]').textContent = fill(summary.last === summary.first ? 'equalSchedule' : 'schedule', { count: term - 1, regularCount: term - 2, first: money(summary.first), last: money(summary.last) });
    modal.querySelector('[data-mt-cart-notice]').textContent = fill('notice', { count: term - 1 });
    modal.querySelector('[data-mt-cart-consent-copy]').textContent = fill('consent', { count: term - 1 });
    const submit = modal.querySelector('[data-mt-cart-pay-submit]');
    submit.textContent = busy ? labels.checking : fill('payFirst', { term });
    submit.disabled = busy || !modal.querySelector('[data-mt-cart-consent]').checked;
  }
  async function openPayment() {
    if (busy) return;
    const opener = document.activeElement;
    loading(true);
    setError();
    try {
      const pending = pendingRead();
      if (pending) await recover(pending);
      await refresh();
      if (!selection.terms.length) throw new Error(selection.reason);
      const defaultTerm = selection.terms.includes(24) ? 24 : selection.terms[0];
      modal.querySelectorAll('[data-mt-cart-term]').forEach(input => { input.closest('label').hidden = !selection.terms.includes(Number(input.value)); input.checked = Number(input.value) === defaultTerm; });
      modal.querySelector('[data-mt-cart-consent]').checked = false;
      modal.querySelector('[data-mt-cart-plan-error]').textContent = '';
      modal.dataset.fingerprint = fingerprint(state);
      originalFocus = opener;
      if (drawer.open) drawer.close();
      modal.showModal();
      lockScroll();
      modal.querySelector('[data-mt-cart-pay-close]').focus({ preventScroll: true });
    } catch (error) { setError(error.message); }
    finally { loading(false); updateModal(); }
  }
  const ownInstallmentKeys = new Set(['Prodotto acquistato a rate', 'Modello e variante', 'Prezzo totale concordato', 'Prima rata versata al checkout', 'Saldo residuo da versare a Media Tech', 'Piano di pagamento', 'Accettazione del saldo']);
  function originalProperties(item) {
    if (item.properties?._mediatech_original_properties) {
      try { return propertiesOf(JSON.parse(item.properties._mediatech_original_properties)); } catch { throw new Error(labels.changed); }
    }
    return Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => !key.startsWith('_mediatech_') && !ownInstallmentKeys.has(key)));
  }
  const snapshotItems = cart => cart.items.map(item => ({ id: item.id, quantity: item.quantity, properties: item.properties || {}, ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) }));
  async function recover(transaction) {
    let current = await readCart();
    const existing = new Map();
    current.items.filter(item => item.properties?._mediatech_cart_transaction !== transaction.id).forEach(item => existing.set(identity(item), (existing.get(identity(item)) || 0) + item.quantity));
    const missing = [];
    for (const item of transaction.originals) {
      const key = identity({ ...item, sellingPlan: item.selling_plan });
      const available = existing.get(key) || 0;
      if (available < item.quantity) missing.push({ ...item, quantity: item.quantity - available });
      existing.set(key, Math.max(0, available - item.quantity));
    }
    if (missing.length) {
      await request('cart/add.js', { items: missing });
      current = await readCart();
    }
    const actual = new Map();
    current.items.filter(item => item.properties?._mediatech_cart_transaction !== transaction.id).forEach(item => actual.set(identity(item), (actual.get(identity(item)) || 0) + item.quantity));
    const expected = new Map();
    transaction.originals.forEach(item => { const key = identity({ ...item, sellingPlan: item.selling_plan }); expected.set(key, (expected.get(key) || 0) + item.quantity); });
    if ([...expected].some(([key, quantity]) => (actual.get(key) || 0) < quantity)) throw new Error(labels.recovery);
    const updates = Object.fromEntries(current.items.filter(item => item.properties?._mediatech_cart_transaction === transaction.id).map(item => [item.key, 0]));
    if (Object.keys(updates).length) await request('cart/update.js', { updates });
    current = await readCart();
    if (current.items.some(item => item.properties?._mediatech_cart_transaction === transaction.id)) throw new Error(labels.recovery);
    pendingWrite(null);
  }
  async function convert(items, expectedFirst, expectedFingerprint) {
    const initial = await readCart();
    if (fingerprint(initial) !== expectedFingerprint) throw new Error(labels.changed);
    const transaction = { id: `mt-${Date.now()}-${crypto.getRandomValues(new Uint32Array(2)).join('-')}`, originals: snapshotItems(initial) };
    pendingWrite(transaction);
    try {
      await request('cart/add.js', { items: items.map(item => ({ ...item, properties: { ...item.properties, _mediatech_cart_transaction: transaction.id } })) });
      let current = await readCart();
      const additions = current.items.filter(item => item.properties?._mediatech_cart_transaction === transaction.id);
      const originalCart = { items: current.items.filter(item => item.properties?._mediatech_cart_transaction !== transaction.id) };
      if (fingerprint(originalCart) !== expectedFingerprint || additions.reduce((sum, item) => sum + item.quantity, 0) !== items.reduce((sum, item) => sum + item.quantity, 0)) throw new Error(labels.changed);
      if (expectedFirst !== null && additions.reduce((sum, item) => sum + item.final_line_price, 0) !== expectedFirst) throw new Error(labels.changed);
      const updates = Object.fromEntries(originalCart.items.map(item => [item.key, 0]));
      await request('cart/update.js', { updates });
      current = await readCart();
      if (current.items.some(item => item.properties?._mediatech_cart_transaction !== transaction.id) || (expectedFirst !== null && current.total_price !== expectedFirst)) throw new Error(labels.changed);
      const expected = new Map();
      items.forEach(item => expected.set(`${item.id}:${sorted(item.properties)}`, (expected.get(`${item.id}:${sorted(item.properties)}`) || 0) + item.quantity));
      const received = new Map();
      current.items.forEach(item => { const props = { ...item.properties }; delete props._mediatech_cart_transaction; const key = `${item.id}:${sorted(props)}`; received.set(key, (received.get(key) || 0) + item.quantity); });
      if (sorted(Object.fromEntries(expected)) !== sorted(Object.fromEntries(received))) throw new Error(labels.changed);
      pendingWrite(null);
      return current;
    } catch (error) {
      try { await recover(transaction); } catch { throw new Error(labels.recovery); }
      throw error;
    }
  }
  async function pay() {
    if (busy || !modal.querySelector('[data-mt-cart-consent]').checked) return;
    await exclusive(async () => {
      const term = Number(modal.querySelector('[data-mt-cart-term]:checked')?.value);
      const consentFingerprint = modal.dataset.fingerprint;
      loading(true);
      updateModal();
      modal.querySelector('[data-mt-cart-plan-error]').textContent = '';
      try {
        await refresh();
        if (fingerprint(state) !== consentFingerprint || !selection.terms.includes(term)) throw new Error(labels.changed);
        const sources = new Map();
        const fees = new Map();
        await limited(selection.lines, async ({ source, variant }) => {
          const key = String(variant.id);
          if (!sources.has(key)) sources.set(key, fetchSource(source.handle, variant.id));
          const plan = variant.plans[String(term)];
          if (!fees.has(String(plan.depositId))) fees.set(String(plan.depositId), fetchSource(plan.depositHandle, plan.depositId));
          const [liveSource, liveFee] = await Promise.all([sources.get(key), fees.get(String(plan.depositId))]);
          const quantity = selection.lines.filter(line => line.variant.id === variant.id).reduce((sum, line) => sum + line.item.quantity, 0);
          const liveVariant = liveSource.source?.variant?.[String(variant.id)];
          if (!liveVariant || quantity < liveVariant.quantityMin || (liveVariant.quantityMax && quantity > liveVariant.quantityMax) || (quantity - liveVariant.quantityMin) % liveVariant.quantityIncrement !== 0) throw new Error(labels.changed);
          if (!liveSource.available || liveSource.price !== variant.totalCents || !liveFee.available || liveFee.price !== plan.depositCents || liveFee.sku !== `M${term}-${variant.id}-${variant.totalCents}`) throw new Error(labels.changed);
          if ((liveSource.tracked && liveSource.policy !== 'continue' && liveSource.stock < quantity) || (liveFee.tracked && liveFee.policy !== 'continue' && liveFee.stock < quantity)) throw new Error(labels.unavailable);
        });
        if (!modal.querySelector('[data-mt-cart-consent]').checked || !modal.open) throw new Error(labels.changed);
        const summary = summarize(term);
        const items = selection.lines.map(({ item, source, variant }) => {
          const plan = variant.plans[String(term)];
          const properties = item.internal || item.properties?._mediatech_payment24 ? originalProperties(item) : item.properties || {};
          return { id: Number(plan.depositId), quantity: item.quantity, properties: {
            ...properties,
            [config.orderLabels.orderProduct]: source.title,
            [config.orderLabels.orderTotal]: `${money(variant.totalCents)} ${config.orderLabels.unit}`,
            [config.orderLabels.orderFirst]: `${money(plan.depositCents)} ${config.orderLabels.unit}`,
            [config.orderLabels.orderRemaining]: `${money(variant.totalCents - plan.depositCents)} ${config.orderLabels.unit}`,
            [config.orderLabels.orderPlan]: fill('orderPlan', { term, count: term - 1 }),
            [config.orderLabels.orderConsent]: fill('orderConsent', { count: term - 1 }),
            ...(variant.title !== 'Default Title' ? { [config.orderLabels.orderOption]: variant.title } : {}),
            _mediatech_source_variant: String(variant.id),
            _mediatech_total_cents_unit: String(variant.totalCents),
            _mediatech_first_cents_unit: String(plan.depositCents),
            _mediatech_last_cents_unit: String(variant.totalCents - plan.depositCents * (term - 1)),
            _mediatech_term: String(term),
            _mediatech_quantity: String(item.quantity),
            _mediatech_payment24: 'v3',
            _mediatech_source_url: source.url,
            _mediatech_original_properties: JSON.stringify(properties)
          } };
        });
        await convert(items, summary.first, consentFingerprint);
        location.assign(url('checkout').href);
      } catch (error) {
        modal.querySelector('[data-mt-cart-plan-error]').textContent = error.message;
        modal.querySelector('[data-mt-cart-consent]').checked = false;
        setError(error.message);
      } finally { loading(false); updateModal(); }
    });
  }
  async function cashCheckout() {
    await exclusive(async () => {
      loading(true);
      setError();
      try {
        const pending = pendingRead();
        if (pending) await recover(pending);
        await refresh();
        if (state.items.some(item => item.internal || item.properties?._mediatech_payment24)) {
          const items = selection.lines.map(({ item, source, variant }) => {
            if (!variant) throw new Error(labels.unavailable);
            return { id: variant.id, quantity: item.quantity, properties: item.internal || item.properties?._mediatech_payment24 ? originalProperties(item) : item.properties || {}, ...(item.sellingPlan ? { selling_plan: item.sellingPlan } : {}) };
          });
          await convert(items, null, fingerprint(state));
        }
        location.assign(url('checkout').href);
      } catch (error) { setError(error.message); loading(false); }
    });
  }
  async function add(data) {
    return exclusive(async () => {
      loading(true);
      setError();
      try {
        const pending = pendingRead();
        if (pending) await recover(pending);
        await request('cart/add.js', data);
        await refresh();
        openDrawer();
        document.dispatchEvent(new CustomEvent('mediatech:cart-updated', { detail: { count: state.count } }));
      } catch (error) { try { await refresh(); } catch {} setError(error.message); throw error; }
      finally { loading(false); }
    });
  }
  async function change(key, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 99) { setError(labels.quantity); return; }
    await exclusive(async () => {
      loading(true);
      setError();
      try {
        const pending = pendingRead();
        if (pending) { await recover(pending); throw new Error(labels.changed); }
        await request('cart/change.js', { id: key, quantity });
        await refresh();
      } catch (error) { try { await refresh(); } catch {} setError(error.message); }
      finally { loading(false); }
    });
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-mt-cart-open]');
    if (opener) { event.preventDefault(); if (!busy) { openDrawer(); loading(true); refresh().catch(error => setError(error.message)).finally(() => loading(false)); } return; }
    if (event.target.closest('[data-mt-cart-close]')) { if (!busy) closeDrawer(); return; }
    if (event.target.closest('[data-mt-cart-pay]')) { event.preventDefault(); openPayment(); return; }
    const remove = event.target.closest('[data-mt-cart-remove]');
    if (remove) { event.preventDefault(); change(remove.dataset.key, 0); return; }
    if (event.target.closest('[data-mt-cart-checkout]')) { event.preventDefault(); if (!busy) cashCheckout(); }
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-mt-cart-quantity]');
    if (input) change(input.dataset.key, Number(input.value));
  });
  drawer.addEventListener('close', () => { unlockScroll(); if (!modal.open && originalFocus?.isConnected) originalFocus.focus({ preventScroll: true }); });
  drawer.addEventListener('cancel', event => { if (busy) event.preventDefault(); });
  modal.addEventListener('cancel', event => { if (busy) event.preventDefault(); });
  modal.addEventListener('close', () => { modal.querySelector('[data-mt-cart-consent]').checked = false; unlockScroll(); if (originalFocus?.isConnected) originalFocus.focus({ preventScroll: true }); });
  modal.querySelector('[data-mt-cart-pay-close]').addEventListener('click', () => { if (!busy) modal.close(); });
  modal.querySelector('[data-mt-cart-pay-submit]').addEventListener('click', pay);
  modal.querySelectorAll('[data-mt-cart-term]').forEach(input => input.addEventListener('change', () => { modal.querySelector('[data-mt-cart-consent]').checked = false; updateModal(); }));
  modal.querySelector('[data-mt-cart-consent]').addEventListener('change', updateModal);
  for (const dialog of [drawer, modal]) dialog.addEventListener('click', event => { if (busy || event.target !== dialog) return; const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); });
  window.MediaTechCart = { add, refresh, open: openDrawer, termsFor };
  if (document.querySelector('[data-mt-cart-content][data-context="page"]')) { loading(true); refresh().catch(error => setError(error.message)).finally(() => loading(false)); }
  addEventListener('pageshow', event => { if (event.persisted) { loading(false); if (modal.open) modal.close(); if (drawer.open) drawer.close(); } });
})();
